import { Helmet } from 'react-helmet';
import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryData, usePostMessage, useDlogEvent } from 'spectacle-ui-game-bridge-react';
import { getPath } from '../../config';
import Build from '../../components/build/build';
import Debug from '../../components/debug/debug';
import ErrorModal from '../../components/errorModal/errorModal';
import ExternalLinkIcon from '../../icons/ExternalLinkIcon';
import LoadingIndicator from '../../components/loadingIndicator/loadingIndicator';
import useBodyBackground from '../../hooks/useBodyBackground';
import useCDNI from '../../hooks/useCDNI';
import useContentForBuild from '../../hooks/useContentForBuild';
import useDlogLoginFunnel from '../../hooks/useDlogLoginFunnel';
import './acceptance.sass';
import { usePreloginContext } from '../../context/PreloginContext';

export default function Acceptance() {
  const navigate = useNavigate();
  const [acceptedCodeOfConductVersion] = useQueryData('game', 'codeofconductversion');
  const [acceptedPrivacyPolicyVersion] = useQueryData('game', 'privacypolicyversion');
  const [acceptedTosVersion] = useQueryData('game', 'tosversion');
  const [acceptedTouVersion] = useQueryData('game', 'touversion');
  const [isCDNIFinished] = useQueryData<boolean>('tos', 'isCDNIFinished');
  const openUrl = usePostMessage('openUrl');
  const dlogAcceptanceShown = useDlogEvent('dlog_event_acceptance_shown');
  const dlog = useDlogLoginFunnel();
  const acceptToS = usePostMessage('acceptToS');
  const acceptToU = usePostMessage('acceptToU');
  const acceptPrivacyPolicy = usePostMessage('acceptPrivacyPolicy');
  const acceptCodeOfConduct = usePostMessage('acceptCodeOfConduct');
  const passPreloginFence = usePostMessage('passPreloginFence');
  const [content, loading, errorContent] = useContentForBuild();
  const { isMobileSharedAccountEnabled } = usePreloginContext();
  useBodyBackground(`url(${getPath('bg_general')}) no-repeat top center fixed #000`);

  const { error } = useCDNI(content);

  const [acceptance, setAcceptance] = useState({
    serviceAcceptance: false,
    termsOfUseAcceptance: false,
    privacyPolicyAcceptance: false,
    codeOfConductAcceptance: false,
  });

  const isButtonDisabled = !acceptance.serviceAcceptance || !acceptance.codeOfConductAcceptance || !acceptance.privacyPolicyAcceptance || !acceptance.termsOfUseAcceptance;

  const handleChange = (e: any) => {
    const value = e.target.checked;
    const name = e.target.name;

    setAcceptance((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleUrl = useCallback(
    (e: any) => {
      e.preventDefault();
      const href = e.target.href;

      openUrl(href);
    },
    [openUrl]
  );

  const handleClickTermsOfUse = (event: any) => {
    dlog({
      phase: 'Legal_URLs',
      action: 'Click',
      targets: 'Terms of Use',
    });
    handleUrl(event);
  };

  const handleClickTermsOfService = (event: any) => {
    dlog({
      phase: 'Legal_URLs',
      action: 'Click',
      targets: 'Terms of Service',
    });
    handleUrl(event);
  };

  const handleClickCodeOfConduct = (event: any) => {
    dlog({
      phase: 'Legal_URLs',
      action: 'Click',
      targets: 'Code of Conduct',
    });
    handleUrl(event);
  };

  const handleClickPrivacyPolicy = (event: any) => {
    dlog({
      phase: 'Legal_URLs',
      action: 'Click',
      targets: 'Privacy Policy',
    });
    handleUrl(event);
  };

  const accept = (e: any) => {
    e.preventDefault();
    acceptToS(content?.serviceAgreementVersion);
    acceptToU(content?.termsOfUseAgreementVersion);
    acceptPrivacyPolicy(content?.privacyPolicyAgreementVersion);
    acceptCodeOfConduct(content?.codeOfConductAgreementVersion);

    dlog({
      phase: 'EULA',
      action: 'Click',
      targets: 'Agree',
    });

    if (content?.webBasedLogin) {
      (window as any).hasPassedAcceptance = true;

      passPreloginFence();

      if (!isCDNIFinished) {
        navigate('/cdni');
      } else {
        if (isMobileSharedAccountEnabled) {
          navigate('/upsell');
        }
        navigate('/login');
      }
    } else {
      passPreloginFence();
    }
  };

  // Set Initial Acceptance
  useEffect(() => {
    if (content) {
      setAcceptance({
        serviceAcceptance: content.serviceAgreementVersion === acceptedTosVersion,
        termsOfUseAcceptance: content.termsOfUseAgreementVersion === acceptedTouVersion,
        privacyPolicyAcceptance: content.privacyPolicyAgreementVersion === acceptedPrivacyPolicyVersion,
        codeOfConductAcceptance: content.codeOfConductAgreementVersion === acceptedCodeOfConductVersion,
      });
    }
  }, [acceptedCodeOfConductVersion, acceptedPrivacyPolicyVersion, acceptedTosVersion, acceptedTouVersion, content]);

  useEffect(() => {
    dlogAcceptanceShown({});
  }, [dlogAcceptanceShown]);

  useEffect(() => {
    dlog({
      phase: 'EULA',
      action: 'Render',
    });
  }, [dlog]);

  return (
    <>
      <Helmet>
        <title>{content?.reviewTerms}</title>
      </Helmet>
      {loading && (
        <div className="loading-mask">
          <LoadingIndicator />
        </div>
      )}
      {content && (
        <div className="centered">
          <div className="acceptance">
            <div className="logo-inline">
              <img src={getPath('menu_cod_logo_lg')} alt="Call of Duty" />
              <h1>{content.reviewTerms}</h1>
            </div>
            <div className="checkbox">
              <input type="checkbox" id="serviceAcceptance" name="serviceAcceptance" checked={acceptance.serviceAcceptance} onChange={handleChange} />
              <div>
                <label htmlFor="serviceAcceptance">
                  <div>{content.serviceAcceptance}</div>
                  <a onClick={handleClickTermsOfService} href={content.serviceAgreementUrl}>
                    {content.serviceCta} <ExternalLinkIcon className="external-link__icon" />
                  </a>
                </label>
              </div>
            </div>
            <div className="checkbox">
              <input type="checkbox" id="termsOfUseAcceptance" name="termsOfUseAcceptance" checked={acceptance.termsOfUseAcceptance} onChange={handleChange} />
              <div>
                <label htmlFor="termsOfUseAcceptance">
                  <div>{content.termsOfUseAcceptance}</div>
                  <a onClick={handleClickTermsOfUse} href={content.termsOfUseAgreementUrl}>
                    {content.termsOfUseCta} <ExternalLinkIcon className="external-link__icon" />
                  </a>
                </label>
              </div>
            </div>
            <div className="checkbox">
              <input type="checkbox" id="privacyPolicyAcceptance" name="privacyPolicyAcceptance" checked={acceptance.privacyPolicyAcceptance} onChange={handleChange} />
              <div>
                <label htmlFor="privacyPolicyAcceptance">
                  <div>{content.privacyPolicyAcceptance}</div>
                  <a onClick={handleClickPrivacyPolicy} href={content.privacyPolicyAgreementUrl}>
                    {content.privacyPolicyCta} <ExternalLinkIcon className="external-link__icon" />
                  </a>
                </label>
              </div>
            </div>
            <div className="checkbox">
              <input type="checkbox" id="codeOfConductAcceptance" name="codeOfConductAcceptance" checked={acceptance.codeOfConductAcceptance} onChange={handleChange} />
              <div>
                <label htmlFor="codeOfConductAcceptance">
                  <div>{content.codeOfConductAcceptance}</div>
                  <a onClick={handleClickCodeOfConduct} href={content.codeOfConductAgreementUrl}>
                    {content.codeOfConductCta} <ExternalLinkIcon className="external-link__icon" />
                  </a>
                </label>
              </div>
            </div>
            <button disabled={isButtonDisabled} className="cta cta--full" onClick={accept}>
              {content.continue}
            </button>
          </div>
        </div>
      )}
      <div className="build">
        <Build />
      </div>
      <ErrorModal error={error} />
      <Debug
        enabled={!!content?.showDeveloperOverlay}
        vars={{
          errorContent,
        }}
      >
        <button onClick={() => window.location.reload()}>reload</button>
      </Debug>
    </>
  );
}
